import { Dialog } from '@mui/material';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import queryString from 'query-string';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as ArrowLeft } from '../../assets/icon/arrow-left.svg';
import { ReactComponent as CreditCardIcon } from '../../assets/icon/credit-card.svg';
import { ReactComponent as MobileCardIcon } from '../../assets/icon/mobile-payment-card.svg';
import Button from '../../components/Button';
import OrderConfirmationDrawer from '../../components/OrderConfirmationDrawer/OrderConfirmationDrawer';
import db_config from '../../configs/db_config';
import { OrderItemStatus, PaymentMethod, SHIPPING_FEE } from '../../consts';
import { useNewOrderContext } from '../../context/NewOrderContext';
import { MenuType, useSystemContext } from '../../context/SystemContext/SystemContext';
import { useUserMenuContext } from '../../context/UserMenuContext';
import { CreateOrderDTO, FoodItem } from '../../interfaces/types';
import { db } from '../../libs/firebase';
import { calculateOrderItemQuantity, calculateOrderItemsTotal, formatPriceItem } from '../../utils';
import { getPreOrderQuantityStatusForCartItem } from '../../utils/preOrderDisabled';
import PAYMENT from '../DeliveryInformation/components/Payment';
import ItemCounter from '../Menu/components/ItemCouter';

interface DeliveryInformationV2Props {}

type PlaceOrderPayload = {
  deliveryRequest: string;
  isSendAsGift: boolean;
  giftInfo?: any;
};

const DeliveryInformationV2: React.FC<DeliveryInformationV2Props> = () => {
  const { t } = useTranslation();
  const { orderDetails, createOrder, updateOrderDetails } = useNewOrderContext();
  const { seatInfo, getFormattedSeat, isPreOrder } = useSystemContext();
  const { listRestaurantWithFood } = useUserMenuContext();

  const navigate = useNavigate();
  const [openPayment, setOpenPayment] = useState(false);
  const [openSeatInfoDrawer, setOpenSeatInfoDrawer] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorFood, setErrorFood] = useState<string>('');
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{ open: boolean; itemIndex: number; itemName: string; isPromotion: boolean }>({
    open: false,
    itemIndex: -1,
    itemName: '',
    isPromotion: false,
  });
  const [maxQuantityDialog, setMaxQuantityDialog] = useState<{ open: boolean; itemName: string; maxQty: number }>({
    open: false,
    itemName: '',
    maxQty: 0,
  });
  const location = useLocation();
  const queryParams = queryString.parse(location.search);

  const [formDataPayment, setFormDataPayment] = useState(PaymentMethod.Credit);
  const orderId = sessionStorage.getItem('orderId');

  const getRestaurantName = (restaurantId: string) => {
    const restaurant = listRestaurantWithFood.find((r) => r.id === restaurantId);
    return restaurant?.name || '';
  };

  const allFoodDetails = useMemo(() => {
    return listRestaurantWithFood.flatMap((restaurant) => restaurant.food_details ?? []);
  }, [listRestaurantWithFood]);

  const getFoodDetail = (foodId: string) => {
    return allFoodDetails.find((food) => food.id === foodId || food.food_id === foodId);
  };

  const getMasterFoodId = (foodId: string) => {
    const foodDetail = getFoodDetail(foodId);
    return foodDetail?.food_id || foodId;
  };

  const reservePreOrderQuantities = async (order: CreateOrderDTO) => {
    if (!isPreOrder || !seatInfo?.order_date) return true;

    const dateKey = seatInfo.order_date.slice(0, 10);
    const quantityByFoodId = new Map<string, { quantity: number; itemName: string }>();

    [...(order.foods ?? []), ...(order.promotion_foods ?? [])].forEach((food: any) => {
      if (!food.item_food_id) return;
      const current = quantityByFoodId.get(food.item_food_id);
      quantityByFoodId.set(food.item_food_id, {
        quantity: (current?.quantity ?? 0) + Number(food.item_quantity ?? 1),
        itemName: current?.itemName || food.item_name,
      });
    });

    if (quantityByFoodId.size === 0) return true;

    let exceededQuantityError:
      | {
          itemName: string;
          remainingQuantity: number;
        }
      | undefined;

    try {
      await db.runTransaction(async (transaction) => {
        const entries = Array.from(quantityByFoodId.entries());
        const snapshots = await Promise.all(entries.map(([foodId]) => transaction.get(db.collection(db_config.DB_TABLE_ALL_FOOD_DETAILS).doc(foodId))));

        snapshots.forEach((snapshot, index) => {
          const [foodId, requested] = entries[index];
          if (!snapshot.exists) {
            throw new Error(`${requested.itemName || foodId} ${t('HAS_BEEN_DISABLE')}`);
          }

          const foodData = snapshot.data() as any;
          const maxQuantity = Number(foodData?.pre_order_quantity || 0);
          const soldQuantity = Number(foodData?.pre_order_sold?.[dateKey] || 0);
          const remainingQuantity = Math.max(0, maxQuantity - soldQuantity);

          if (maxQuantity > 0 && requested.quantity > remainingQuantity) {
            exceededQuantityError = {
              itemName: requested.itemName,
              remainingQuantity,
            };
            throw new Error('PRE_ORDER_MAX_QUANTITY_EXCEEDED');
          }
        });

        snapshots.forEach((snapshot, index) => {
          const [, requested] = entries[index];
          transaction.update(snapshot.ref, {
            [`pre_order_sold.${dateKey}`]: firebase.firestore.FieldValue.increment(requested.quantity),
          });
        });
      });

      return true;
    } catch (error: any) {
      const exceeded = exceededQuantityError;
      if (exceeded) {
        setMaxQuantityDialog({
          open: true,
          itemName: exceeded.itemName,
          maxQty: exceeded.remainingQuantity,
        });
      } else if (error?.message !== 'PRE_ORDER_MAX_QUANTITY_EXCEEDED') {
        console.error('pre_order_sold reservation error', error);
      }
      return false;
    }
  };

  const [price, totalPrice, totalItem] = useMemo(() => {
    if (!orderDetails) return [0, 0, 0];
    const totalItem = calculateOrderItemQuantity(orderDetails);
    const totalPrice = calculateOrderItemsTotal(orderDetails);

    const formattedPrice = totalPrice;
    const totalPayment = totalPrice + SHIPPING_FEE;

    return [formattedPrice, totalPayment, totalItem];
  }, [orderDetails]);

  const handleClosePayment = () => {
    setOpenPayment(false);
  };

  const handleSubmitPaymentWithOrder = async (od?: CreateOrderDTO) => {
    const odToUse = od ?? orderDetails;
    if (!odToUse || !orderId || !seatInfo?.id) return false;

    if (odToUse.promotion_foods?.length > 0) {
      odToUse.food_order_status = OrderItemStatus.PREPARING;
      odToUse.food_promotion_order_status = OrderItemStatus.PREPARING;
    }

    try {
      const isErrorOrder = await createOrder(odToUse, orderId);
      if (!isErrorOrder) {
        const errorFoodItem = sessionStorage.getItem('ErrorFood');
        if (errorFoodItem) setErrorFood(errorFoodItem);
        setOpenError(true);
        return false;
      }
      return true;
    } catch (err) {
      console.error('handleSubmitPaymentWithOrder error', err);
      return false;
    }
  };

  const handlePlaceOrder = async ({ deliveryRequest, isSendAsGift, giftInfo }: PlaceOrderPayload) => {
    if (loading || !seatInfo?.email) return;

    setLoading(true);
    const selectedDeliveryRequest = isPreOrder ? orderDetails.delivery_request || deliveryRequest : deliveryRequest;
    const foods = orderDetails?.foods ?? [];
    const restaurantIds: string[] = Array.from(new Set(foods.map((f: any) => f.restaurantId ?? f.restaurant_id).filter(Boolean)));

    const updated: CreateOrderDTO = {
      ...(orderDetails as CreateOrderDTO),
      payment_method: formDataPayment,
      requested_delivery_time: firebase.firestore.Timestamp.fromDate(new Date(seatInfo.order_date)),
      delivery_request: selectedDeliveryRequest,
    } as CreateOrderDTO;

    if (restaurantIds.length > 0) {
      updated.restaurant_ids = restaurantIds;

      updated.multi_orders = restaurantIds.map((id) => ({
        restaurant_id: id,
        status: OrderItemStatus.PREPARING,
      }));
    } else {
      updated.restaurant_id = restaurantIds[0] || '';
    }
    if (isSendAsGift && giftInfo) {
      updated.is_gift = true;
      updated.gift_info = giftInfo;
    }
    if (seatInfo.email) updated.email = seatInfo.email;
    if (seatInfo.phone) updated.phone = seatInfo.phone;

    updated.is_pre_order = isPreOrder;
    updated.order_type = isPreOrder
      ? seatInfo?.special_order
        ? MenuType.SPECIAL_PRE_ORDER
        : MenuType.REGULAR_PRE_ORDER
      : seatInfo?.special_order
        ? MenuType.SPECIAL_ORDER
        : MenuType.REGULAR_ORDER;

    updateOrderDetails(updated);

    const isOrderValid = await handleSubmitPaymentWithOrder(updated);
    if (isOrderValid) {
      const isPreOrderQuantityReserved = await reservePreOrderQuantities(updated);
      if (isPreOrderQuantityReserved) {
        setOpenPayment(true);
      } else {
        const createdOrderId = sessionStorage.getItem('orderId') || orderId;
        if (createdOrderId) {
          await db.collection(db_config.DB_TABLE_ALL_ORDERS).doc(createdOrderId).delete().catch((error: any) => {
            console.error('delete unreserved pre-order error', error);
          });
        }
      }
    }
    setLoading(false);
  };

  const handleCloseErrorModal = () => {
    setOpenError(false);
    if (errorFood) {
      const updated = {
        ...orderDetails,
        foods: [],
        promotion_restaurant_id: '',
        promotion_foods: [],
      };
      if (updated.hasOwnProperty('food_order_status')) {
        delete updated.food_order_status;
      }
      if (updated.hasOwnProperty('food_promotion_order_status')) {
        delete updated.food_promotion_order_status;
      }
      updateOrderDetails(updated);
    }
    sessionStorage.removeItem('ErrorFood');
    const neworderDetailLink = queryString.stringifyUrl({
      url: `/neworder`,
      query: { ...queryParams, tab: 'home' },
    });
    navigate(neworderDetailLink);
  };

  const handleBackToMenu = () => {
    navigate(-1);
  };

  const handleAddItem = () => {
    const neworderDetailLink = queryString.stringifyUrl({
      url: `/neworder`,
      query: { ...queryParams, tab: 'home' },
    });
    navigate(neworderDetailLink);
  };

  const canUpdatePreOrderQuantity = (item: FoodItem, newQuantity: number) => {
    if (!isPreOrder || newQuantity <= 0) return true;

    const foodDetail = getFoodDetail(item.item_food_id);
    if (!foodDetail) return true;

    const allOrderFoods = [...(orderDetails.foods ?? []), ...(orderDetails.promotion_foods ?? [])];
    const quantityStatus = getPreOrderQuantityStatusForCartItem({
      food: foodDetail,
      orderDate: seatInfo?.order_date,
      cartFoods: allOrderFoods,
      itemFoodId: item.item_food_id,
      newQuantity,
      getMasterFoodId,
    });
    if (!quantityStatus.exceedsLimit) return true;

    setMaxQuantityDialog({
      open: true,
      itemName: item.item_name,
      maxQty: quantityStatus.remainingQuantity,
    });
    return false;
  };

  const handleQuantityChange = (itemIndex: number, newQuantity: number) => {
    const item = orderDetails.foods[itemIndex];
    if (item && !canUpdatePreOrderQuantity(item, newQuantity)) return;

    if (newQuantity <= 0) {
      const updatedFoods = orderDetails.foods.filter((_: any, index: number) => index !== itemIndex);
      updateOrderDetails({ ...orderDetails, foods: updatedFoods });
    } else {
      const updatedFoods = orderDetails.foods.map((food: FoodItem, index: number) => {
        if (index === itemIndex) {
          const newSubtotal = food.item_price * newQuantity;
          return {
            ...food,
            item_quantity: newQuantity,
            item_subtotal: newSubtotal,
          };
        }
        return food;
      });
      updateOrderDetails({ ...orderDetails, foods: updatedFoods });
    }
  };

  const handlePromotionQuantityChange = (itemIndex: number, newQuantity: number) => {
    const item = orderDetails.promotion_foods[itemIndex];
    if (item && !canUpdatePreOrderQuantity(item, newQuantity)) return;

    if (newQuantity <= 0) {
      const updatedPromotionFoods = orderDetails.promotion_foods.filter((_: any, index: number) => index !== itemIndex);
      updateOrderDetails({ ...orderDetails, promotion_foods: updatedPromotionFoods });
    } else {
      const updatedPromotionFoods = orderDetails.promotion_foods.map((food: FoodItem, index: number) => {
        if (index === itemIndex) {
          const newSubtotal = food.item_price * newQuantity;
          return {
            ...food,
            item_quantity: newQuantity,
            item_subtotal: newSubtotal,
          };
        }
        return food;
      });
      updateOrderDetails({ ...orderDetails, promotion_foods: updatedPromotionFoods });
    }
  };

  const handleDeleteClick = (itemIndex: number) => {
    const item = orderDetails.foods[itemIndex];
    setDeleteConfirmDialog({
      open: true,
      itemIndex,
      itemName: item?.item_name || '',
      isPromotion: false,
    });
  };

  const handlePromotionDeleteClick = (itemIndex: number) => {
    const item = orderDetails.promotion_foods[itemIndex];
    setDeleteConfirmDialog({
      open: true,
      itemIndex,
      itemName: item?.item_name || '',
      isPromotion: true,
    });
  };

  const handleConfirmDelete = () => {
    if (deleteConfirmDialog.isPromotion) {
      const updatedPromotionFoods = orderDetails.promotion_foods.filter((_: any, index: number) => index !== deleteConfirmDialog.itemIndex);
      updateOrderDetails({ ...orderDetails, promotion_foods: updatedPromotionFoods });
    } else {
      const updatedFoods = orderDetails.foods.filter((_: any, index: number) => index !== deleteConfirmDialog.itemIndex);
      updateOrderDetails({ ...orderDetails, foods: updatedFoods });
    }
    setDeleteConfirmDialog({ open: false, itemIndex: -1, itemName: '', isPromotion: false });
  };

  const handleCancelDelete = () => {
    setDeleteConfirmDialog({ open: false, itemIndex: -1, itemName: '', isPromotion: false });
  };

  const handleCloseMaxQuantityDialog = () => {
    setMaxQuantityDialog({ open: false, itemName: '', maxQty: 0 });
  };

  useEffect(() => {
    const checkOrderDetails = async () => {
      if (orderDetails.foods.length === 0 && orderDetails.promotion_foods.length === 0) {
        if (!orderId) {
          const homeLink = queryString.stringifyUrl({
            url: `/neworder`,
            query: { ...queryParams, tab: 'home' },
          });
          navigate(homeLink);
        } else {
          const orderHistory = await db.collection(db_config.DB_TABLE_ALL_ORDERS).doc(orderId).get();
          if (orderHistory.exists) {
            const orderDetailLink = queryString.stringifyUrl({
              url: `/orderdetail/${orderId}`,
              query: queryParams,
            });
            window.location.href = orderDetailLink;
          } else {
            const newOrderDetailLink = queryString.stringifyUrl({
              url: `/neworder`,
              query: { ...queryParams, tab: 'home' },
            });
            navigate(newOrderDetailLink);
          }
        }
      }
    };

    checkOrderDetails();
  }, [orderDetails, orderId]);

  const totalQuantity = useMemo(() => {
    const promotion_foods = orderDetails?.promotion_foods ?? [];
    const foods = orderDetails?.foods ?? [];
    const allFoods = [...promotion_foods, ...foods];
    return allFoods.reduce((sum: number, food: any) => sum + (food.item_quantity || 0), 0);
  }, [orderDetails]);

  return (
    <div className="w-[auto] min-h-[100dvh] bg-black-0">
      <div className="max-w-[672px] min-h-[100dvh] mx-auto bg-white">
        <div className="flex w-full justify-center sticky top-0 bg-white gap-2 items-center px-4 border-b">
          <button className="absolute left-3 p-3" onClick={handleBackToMenu}>
            <ArrowLeft />
          </button>
          <p className="text-gray-700 text-base font-medium leading-6 p-3">{t('DELIVERY_TITLE')}</p>
        </div>

        <div className="p-4 flex justify-between border-b gap-1">
          <div className="flex flex-col gap-1 flex-1">
            <p className="text-black-500 text-sm leading-5 font-medium">{t('HOME_SEAT_TITLE')}</p>
            <p className="text-gray-800 font-medium leading-7 text-sm">{getFormattedSeat?.(orderDetails?.order_table_number) || '-'}</p>
          </div>
          <div className="flex flex-col gap-1 flex-1">
            <p className="text-black-500 text-sm leading-5 font-medium">{t('HOME_ORDERID_TITLE')}</p>
            <p className="text-gray-800 font-medium leading-7 text-sm">{orderId}</p>
          </div>
        </div>

        <div className="flex justify-between py-3 px-4">
          <div className="flex justify-start text-sm leading-5">
            <p className="text-black-500 font-medium border-r border-r-[#EAECF0] pr-2">{t('DELIVERY_ORDER_SUMMARY')}</p>
            <p className="text-black-500 font-medium pl-2">
              {totalQuantity} {t('MENU_CARD_ITEMS')}
            </p>
          </div>
          <div onClick={handleAddItem} className="text-green-700 font-semibold">
            {t('DELIVERY_ORDER_ADD_ITEM')}
          </div>
        </div>

        <div className="">
          {orderDetails &&
            orderDetails.foods.map((item: any, index: any) => {
              const restaurantName = item.restaurantId ? getRestaurantName(item.restaurantId) : '';
              return (
                <div key={index} className="flex justify-between gap-4 p-4 border-b">
                  <div className="flex-1 flex gap-2">
                    <div className="flex flex-col gap-2 flex-1">
                      <p className="text-black-800 text-xs not-italic font-medium leading-[18px]">{item.item_name}</p>
                      {restaurantName && <p className="text-gray-500 text-xs not-italic font-normal leading-[18px]">{restaurantName}</p>}
                      {item.item_customize_settings && item.item_customize_settings.length > 0 && (
                        <div className="flex gap-2 pb-1 text-[10px] not-italic font-normal leading-[150%] text-black-500 flex-col">
                          {item.item_customize_settings.map((topping: any, toppingIndex: any) => (
                            <React.Fragment key={toppingIndex}>
                              <p>- {topping.name}</p>
                            </React.Fragment>
                          ))}
                        </div>
                      )}
                      {item.item_note && <p className="text-black-400 text-xs font-normal leading-[18px]">{item.item_note}</p>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <p className="w-fit text-black-500 text-xs font-normal leading-[18px]">¥ {formatPriceItem(item.item_subtotal)}</p>
                    <ItemCounter
                      count={item.item_quantity}
                      setCount={(newCount) => handleQuantityChange(index, newCount)}
                      initialCountItem={1}
                      onDelete={() => handleDeleteClick(index)}
                    />
                  </div>
                </div>
              );
            })}
          {orderDetails &&
            orderDetails.promotion_foods.map((item: any, index: any) => {
              const restaurantName = item.restaurantId
                ? getRestaurantName(item.restaurantId)
                : orderDetails.promotion_restaurant_id
                  ? getRestaurantName(orderDetails.promotion_restaurant_id)
                  : '';
              return (
                <div key={index} className="flex justify-between gap-4 p-4 border-b">
                  <div className="flex-1 flex gap-2">
                    <div className="flex flex-col gap-2 flex-1">
                      <p className="text-black-800 text-xs not-italic font-medium leading-[18px]">{item.item_name}</p>
                      {restaurantName && <p className="text-gray-500 text-xs not-italic font-normal leading-[18px]">{restaurantName}</p>}
                      {item.item_customize_settings && item.item_customize_settings.length > 0 && (
                        <div className="flex gap-2 pb-1 text-[10px] not-italic font-normal leading-[150%] text-black-500 flex-col">
                          {item.item_customize_settings.map((topping: any, toppingIndex: number) => (
                            <React.Fragment key={toppingIndex}>
                              <p>- {topping.name}</p>
                            </React.Fragment>
                          ))}
                        </div>
                      )}
                      {item.item_note && <p className="text-black-400 text-xs font-normal leading-[18px]">{item.item_note}</p>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <p className="w-fit text-black-500 text-xs font-normal leading-[18px]">¥ {formatPriceItem(item.item_subtotal)}</p>
                    <ItemCounter
                      count={item.item_quantity}
                      setCount={(newCount) => handlePromotionQuantityChange(index, newCount)}
                      initialCountItem={1}
                      onDelete={() => handlePromotionDeleteClick(index)}
                    />
                  </div>
                </div>
              );
            })}
        </div>

        <div className="pb-[130px] bg-white">
          <div className="py-3 px-4">
            <p className="text-gray-500 text-sm font-medium leading-5">{t('DELIVERY_ORDER_PAYMENT')}</p>
          </div>
          <div className="py-2 px-4">
            <div className="flex justify-between">
              <p className="text-gray-700 text-xs font-normal leading-[18px]">{t('ORDER_PRICE')}</p>
              <p className="text-black-500 text-xs font-normal leading-[18px]">¥ {formatPriceItem(price)}</p>
            </div>
          </div>

          <div className="px-4">
            <div className="flex justify-between border-b py-2">
              <p className="text-gray-700 text-xs font-normal leading-[18px]">{t('ORDER_DELIVERY_FEE')}</p>
              <p className="text-black-500 text-xs font-normal leading-[18px]">¥ {formatPriceItem(SHIPPING_FEE)}</p>
            </div>
          </div>

          <div className="px-4">
            <div className="flex justify-between text-black-800 text-sm font-semibold leading-5 border-b py-4">
              <p className="">{t('ORDER_TOTAL_PAYMENT')}</p>
              <div className="flex gap-[2px]">
                <p>¥ {formatPriceItem(totalPrice)}</p> <p className="text-black-400 font-light">{t('TAX_INCLUDED')}</p>
              </div>
            </div>
          </div>
          <div className="py-3 px-4">
            <p className="text-gray-500 text-sm font-medium leading-5">{t('ORDER_PAYMENT_METHOD')}</p>
          </div>

          <div className="p-4 flex justify-between items-center">
            <div className="flex gap-2 items-center">
              <CreditCardIcon />
              <p className="text-gray-700 text-sm font-medium leading-5">{t(PaymentMethod.Credit)}</p>
            </div>
          </div>
          <div className="px-4 flex justify-between items-center">
            <div className="flex gap-2 items-center">
              <MobileCardIcon />
              <p className="text-gray-700 text-sm font-medium leading-5">{t(PaymentMethod.Mobile)}</p>
            </div>
          </div>
          <PAYMENT open={openPayment} onClose={handleClosePayment} totalPrice={totalPrice} />
        </div>
        <div className="flex flex-col gap-3 fixed bottom-0 bg-white w-full max-w-[672px] p-4">
          <div className="flex justify-between">
            <label htmlFor="quantity" className="text-sm font-medium leading-5 text-gray-500">
              {totalItem} {t('MENU_CARD_ITEMS')}
            </label>
            <div className="flex gap-1">
              <p className="text-black-800 text-base font-semibold leading-6">¥ {formatPriceItem(totalPrice)}</p>
              <p className="text-black-400 font-light">{t('TAX_INCLUDED')}</p>
            </div>
          </div>
          <Button type="secondary" onClick={() => setOpenSeatInfoDrawer(true)}>
            {t('ORDER_BUTTON')}
          </Button>
        </div>

        <Dialog open={openError} onClose={handleCloseErrorModal} sx={{ '& .MuiPaper-root': { borderRadius: '12px' } }}>
          <div className="p-6 flex flex-col gap-6">
            <div className="flex flex-col gap-1">
              <p className="text-black-900 text-lg font-semibold leading-7">{errorFood ? `${errorFood} ${t('HAS_BEEN_DISABLE')}` : t('LISTRESTAURANT_MODAL_TITLE')}</p>
            </div>
          </div>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmDialog.open} onClose={handleCancelDelete} sx={{ '& .MuiPaper-root': { borderRadius: '12px' } }}>
          <div className="p-12 flex flex-col gap-6 w-full items-center">
            <p className="text-black-900 text-lg font-semibold leading-7 text-center">{t('CONFIRM_DELETE_ITEM')}</p>
            {deleteConfirmDialog.itemName && <p className="text-gray-700 text-base font-medium text-center">{deleteConfirmDialog.itemName}</p>}
            <div className="flex gap-4 w-full">
              <button
                className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 text-base font-semibold leading-6 bg-white text-gray-700"
                onClick={handleCancelDelete}
              >
                {t('CANCEL_BUTTON')}
              </button>
              <button
                className="flex-1 px-4 py-2.5 rounded-lg border border-solid shadow-button text-base font-semibold leading-6 bg-red-500 border-red-500 text-white"
                onClick={handleConfirmDelete}
              >
                {t('DELETE_BUTTON')}
              </button>
            </div>
          </div>
        </Dialog>

        <Dialog open={maxQuantityDialog.open} onClose={handleCloseMaxQuantityDialog} sx={{ '& .MuiPaper-root': { borderRadius: '12px', width: '100%' } }}>
          <div className="p-6 flex flex-col gap-3 w-full items-center">
            <p className="text-black-900 text-lg font-semibold leading-7 text-center">{t('PRE_ORDER_MAX_QUANTITY') || 'Maximum pre-order quantity reached'}</p>
            <p className="text-gray-500 text-sm text-center">
              {t('PRE_ORDER_MAX_QUANTITY_DESC', {
                food_name: maxQuantityDialog.itemName,
                quantity: maxQuantityDialog.maxQty,
                defaultValue: 'You cannot order more than {{quantity}} items for {{food_name}}.',
              })}
            </p>
            <button
              className="px-18 mt-2 py-2.5 rounded-lg border border-solid shadow-button text-base font-semibold leading-6 bg-primary-green border-primary-green text-white"
              onClick={handleCloseMaxQuantityDialog}
            >
              {t('OK')}
            </button>
          </div>
        </Dialog>

        <OrderConfirmationDrawer open={openSeatInfoDrawer} onClose={() => setOpenSeatInfoDrawer(false)} onConfirm={handlePlaceOrder} />
      </div>
    </div>
  );
};

export default DeliveryInformationV2;
